import React from 'react';
import styled from 'styled-components';
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  variant
} from 'styled-system';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { KompisText } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

// @see https://fundament.ikea.com/foundation/typography#typeStyles
type Type =
  | ThemeFontStyleTypeEnum.bodySmall
  | ThemeFontStyleTypeEnum.bodyMedium
  | ThemeFontStyleTypeEnum.bodyLarge
  | ThemeFontStyleTypeEnum.bodyXLarge
  | ThemeFontStyleTypeEnum.priceLarge;

const Box = styled('p')`
  margin: 0;
  word-break: break-word;
  ${color}
  ${space}
  ${layout}
  ${typography}
  ${variant({
    variants: {
      body12: {
        lineHeight: 'short'
      },
      body14: {
        lineHeight: 'shorter'
      },
      body16: {
        lineHeight: 'base'
      },
      body18: {
        lineHeight: 'base'
      }
    }
  })}
`;

type AsProps = {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  forwardedAs?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
};

type ExtendProps = React.PropsWithoutRef<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
> &
  SpaceProps &
  ColorProps &
  TypographyProps &
  LayoutProps &
  AsProps;

export interface BodyProps extends ExtendProps {
  /** Type of body text as described in Fundament */
  type: Type;
  /** React element, text, or fragment */
  children: React.ReactNode;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

const Body = ({ type, bold, italic, children, color = 'text', ...props }: BodyProps) => {
  return (
    <Box {...props} color={color} variant={type}>
      <KompisText type={type} bold={bold} italic={italic} theme={SkapaTheme}>
        {children}
      </KompisText>
    </Box>
  );
};

export default Body;
